import { designVariables } from "@studio3t/design-variables";
import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { OverlayScroll } from "@/components/OverlayScroll";
import { SkeletonBar } from "@/components/Skeleton";
import { SortableHeader } from "@/components/SortableHeader";
import { TablePagination } from "@/components/TablePagination";
import type { JobWorkerMetrics, WorkerMetrics } from "@/types/api";
import { formatBytes, formatNumber, formatRate } from "@/utils/format";
import { userStorage } from "@/utils/userStorage";
import { TabEmptyState } from "./styles";

const TABLE = designVariables.ui.table;
const SINGLE_LINE = designVariables.type.singleLine;

// Page size 10 by default; the bar only appears once there are MORE than 10
// workers (10 → no bar, 11 → bar), matching the spec. Sizes mirror the
// Pipelines list selector.
const PAGE_SIZE = 10;
const PAGE_SIZES = [10, 25, 50, 100];

// Persist the chosen page size (user preference, shared across pipelines) the
// same way the Pipelines list does — per-user-scoped localStorage, stored as an
// object so the shape can grow later.
const WORKERS_TABLE_SETTINGS_KEY = "workers-table";

function loadWorkersPageSize(): number {
  try {
    const raw = userStorage.getItem(WORKERS_TABLE_SETTINGS_KEY);
    if (!raw) return PAGE_SIZE;
    const parsed: unknown = JSON.parse(raw);
    const size = (parsed as { pageSize?: unknown } | null)?.pageSize;
    return PAGE_SIZES.includes(size as number) ? (size as number) : PAGE_SIZE;
  } catch {
    return PAGE_SIZE;
  }
}

function saveWorkersPageSize(pageSize: number): void {
  userStorage.setItem(WORKERS_TABLE_SETTINGS_KEY, JSON.stringify({ pageSize }));
}

interface WorkersBodyProps {
  data: JobWorkerMetrics | undefined;
  loading: boolean;
  error: unknown;
  isRunning: boolean;
}

function formatLastActive(iso: string | null): string {
  if (!iso) return "—";
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return "—";
  const diff = Date.now() - ts;
  if (diff < 0) return "just now";
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}

// Token-driven table grid per Figma nodes 13495-42668 / 13022-6071 (matches the
// Pipelines list table): 1px rules in table/background between white cells, 30px
// header row, 50px body rows. border-spacing 0 keeps the rules aligned under the
// rounded card without collapsing.
const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background: ${(props) => props.theme.tableTheadCellBackground};
  table-layout: auto;

  tbody tr:last-child td {
    border-bottom: none;
  }

  th:last-child,
  td:last-child {
    border-right: none;
  }
`;

// Figma gives every column a 120px minimum so the metric columns spread evenly
// rather than hugging their (short) numeric content; the Node column overrides
// this to absorb the remaining width.
const COLUMN_MIN_WIDTH = 120;

// No border-top: the table sits flush under the tab bar, whose bottom border is
// the shared top edge (otherwise the header's top rule doubles up with it).
const Th = styled.th`
  text-align: left;
  height: ${TABLE.thead.cell.height}px;
  min-width: ${COLUMN_MIN_WIDTH}px;
  padding: 0 ${TABLE.thead.cell.paddingr}px 0 ${TABLE.thead.cell.paddingl}px;
  font-size: ${SINGLE_LINE.small.regular.fontSize}px;
  font-weight: ${SINGLE_LINE.small.regular.fontWeight};
  color: ${(props) => props.theme.semanticTextLight};
  white-space: nowrap;
  background: ${(props) => props.theme.tableTheadCellBackground};
  border-bottom: 1px solid ${(props) => props.theme.tableBackground};
  border-right: 1px solid ${(props) => props.theme.tableBackground};
`;

// Cell + CellText pattern mirrors the Pipelines list table: the cell owns the
// 4px padding, borders and vertical centering; the inner CellText adds the 6px
// gap and handles ellipsis (Figma table/tbody/cell padding + gap).
const Td = styled.td`
  height: ${TABLE.tbody.cell.heightm}px;
  min-width: ${COLUMN_MIN_WIDTH}px;
  padding: 0 ${TABLE.tbody.cell.padding}px;
  font-size: ${SINGLE_LINE.default.regular.fontSize}px;
  color: ${(props) => props.theme.semanticTextBody};
  vertical-align: middle;
  border-bottom: 1px solid ${(props) => props.theme.tableBackground};
  border-right: 1px solid ${(props) => props.theme.tableBackground};
  overflow: hidden;
`;

// The Node column absorbs slack and ellipsizes long pod/worker names; the
// max-width:0 + width:100% trick lets the cell shrink-grow in an auto table.
const NodeTd = styled(Td)`
  width: 100%;
  max-width: 0;
`;

const CellText = styled.span`
  display: block;
  padding: 0 ${TABLE.tbody.cell.gap}px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Tr = styled.tr`
  &:hover {
    background: ${(props) => props.theme.tableTbodyCellHoverBackground};
  }
`;

function emptyMessage(isRunning: boolean, hasError: boolean): string | null {
  if (hasError) return "Failed to load worker metrics";
  if (isRunning) return "No worker metrics available yet";
  return "Workers report metrics while the pipeline is running";
}

type SortKey = "node" | "events" | "bytes" | "throughput" | "errors" | "last";
type SortState = { key: SortKey; dir: "asc" | "desc" };

// Column metadata drives both the sortable headers and the comparator. The cell
// rendering stays explicit below (each column formats differently); the order
// here must match the order of the body cells.
const COLUMNS: {
  key: SortKey;
  label: string;
  value: (w: WorkerMetrics) => string | number;
}[] = [
  {
    key: "node",
    label: "Node",
    value: (w) => (w.node_name || w.worker_id).toLowerCase(),
  },
  {
    key: "events",
    label: "Events",
    value: (w) => w.session_events_processed ?? w.events_processed ?? 0,
  },
  {
    key: "bytes",
    label: "Bytes",
    value: (w) => w.session_bytes_processed ?? w.bytes_processed ?? 0,
  },
  {
    key: "throughput",
    label: "Throughput",
    value: (w) => w.throughput_per_second ?? 0,
  },
  { key: "errors", label: "Errors", value: (w) => w.total_errors ?? 0 },
  {
    key: "last",
    label: "Last active",
    // Nulls sort as the oldest so active workers lead a descending sort.
    value: (w) => {
      const ts = w.last_processed_at
        ? new Date(w.last_processed_at).getTime()
        : Number.NEGATIVE_INFINITY;
      return Number.isNaN(ts) ? Number.NEGATIVE_INFINITY : ts;
    },
  },
];

function ariaSort(
  sort: SortState | null,
  key: SortKey,
): "ascending" | "descending" | "none" {
  if (sort?.key !== key) return "none";
  return sort.dir === "asc" ? "ascending" : "descending";
}

function WorkerHeaderCell({
  col,
  sort,
  onSort,
}: {
  col: (typeof COLUMNS)[number];
  sort: SortState | null;
  onSort: (key: SortKey) => void;
}) {
  return (
    <Th aria-sort={ariaSort(sort, col.key)}>
      <SortableHeader
        direction={sort?.key === col.key ? sort.dir : null}
        onClick={() => onSort(col.key)}
      >
        {col.label}
      </SortableHeader>
    </Th>
  );
}

function WorkerTable({
  workers,
  sort,
  onSort,
}: {
  workers: WorkerMetrics[];
  sort: SortState | null;
  onSort: (key: SortKey) => void;
}) {
  return (
    // Horizontal scroll with the app's custom scrollbars once the columns hit
    // their min-widths; pagination owns vertical, so only the horizontal bar
    // ever activates.
    <OverlayScroll>
      <Table>
        <thead>
          <tr>
            {COLUMNS.map((col) => (
              <WorkerHeaderCell
                key={col.key}
                col={col}
                sort={sort}
                onSort={onSort}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {workers.map((w) => (
            <Tr key={w.worker_id}>
              <NodeTd>
                <CellText title={w.node_name || w.worker_id}>
                  {w.node_name || w.worker_id}
                </CellText>
              </NodeTd>
              <Td>
                <CellText>
                  {formatNumber(
                    w.session_events_processed ?? w.events_processed ?? 0,
                  )}
                </CellText>
              </Td>
              <Td>
                <CellText>
                  {formatBytes(
                    w.session_bytes_processed ?? w.bytes_processed ?? 0,
                  )}
                </CellText>
              </Td>
              <Td>
                <CellText>
                  {formatRate(w.throughput_per_second)} events/sec
                </CellText>
              </Td>
              <Td>
                <CellText>{formatNumber(w.total_errors)}</CellText>
              </Td>
              <Td>
                <CellText>{formatLastActive(w.last_processed_at)}</CellText>
              </Td>
            </Tr>
          ))}
        </tbody>
      </Table>
    </OverlayScroll>
  );
}

// Three-state toggle per column (asc → desc → unsorted), matching the Pipelines
// list.
function nextSortState(prev: SortState | null, key: SortKey): SortState | null {
  if (!prev || prev.key !== key) return { key, dir: "asc" };
  if (prev.dir === "asc") return { key, dir: "desc" };
  return null;
}

function compareValues(av: string | number, bv: string | number): number {
  if (typeof av === "number" && typeof bv === "number") return av - bv;
  return String(av).localeCompare(String(bv));
}

// Sort across the whole list before paging so the order spans pages (not just
// the visible slice). A stable copy keeps the poll-driven array intact.
function sortWorkers(
  workers: WorkerMetrics[],
  sort: SortState | null,
): WorkerMetrics[] {
  const col = sort && COLUMNS.find((c) => c.key === sort.key);
  if (!sort || !col) return workers;
  const dir = sort.dir === "asc" ? 1 : -1;
  return [...workers].sort(
    (a, b) => dir * compareValues(col.value(a), col.value(b)),
  );
}

// Owns the sort + pagination state for the table, keeping the component itself a
// flat render. The bar only shows once there are more than 10 workers (spec: 10
// → no bar, 11 → bar); the threshold is the default page size (not the selected
// one) so the bar can't disappear after the user picks a larger size.
function useWorkerTable(workers: WorkerMetrics[]) {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(loadWorkersPageSize);
  const [sort, setSort] = useState<SortState | null>(null);

  const toggleSort = (key: SortKey) => {
    setSort((prev) => nextSortState(prev, key));
    setPageIndex(0);
  };
  const changePageSize = (size: number) => {
    setPageSize(size);
    setPageIndex(0);
    saveWorkersPageSize(size);
  };

  const sortedWorkers = useMemo(
    () => sortWorkers(workers, sort),
    [workers, sort],
  );
  const pageCount = Math.max(1, Math.ceil(workers.length / pageSize));

  // The list re-polls every 5s and can shrink under the current page; clamp the
  // index back into range when that happens (mirrors Pipelines' safePage).
  const safePage = Math.min(pageIndex, pageCount - 1);
  useEffect(() => {
    if (pageIndex !== safePage) setPageIndex(safePage);
  }, [pageIndex, safePage]);

  const paginate = workers.length > PAGE_SIZE;
  return {
    sort,
    toggleSort,
    paginate,
    pageIndex: safePage,
    pageCount,
    pageSize,
    total: workers.length,
    visibleWorkers: paginate
      ? sortedWorkers.slice(safePage * pageSize, (safePage + 1) * pageSize)
      : sortedWorkers,
    setPageIndex,
    changePageSize,
  };
}

// The Workers tab body (the worker table + pagination, or an empty state). The
// polling and the tab's count badge live in the parent SectionTabs so the count
// can show on the tab itself; this component just renders the polled data.
// Skeleton body mirrors the worker table shape (same header cells, bar-filled
// rows) so the swap to data doesn't shift the columns.
const SKELETON_ROWS = Array.from({ length: 4 }, (_, i) => `skeleton-${i}`);

function WorkersSkeleton({ sort }: { sort: SortState | null }) {
  return (
    <OverlayScroll aria-hidden="true">
      <Table>
        <thead>
          <tr>
            {COLUMNS.map((col) => (
              <WorkerHeaderCell
                key={col.key}
                col={col}
                sort={sort}
                onSort={() => {}}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {SKELETON_ROWS.map((key) => (
            <Tr key={key}>
              <NodeTd>
                <SkeletonBar $width="70%" />
              </NodeTd>
              {COLUMNS.slice(1).map((col) => (
                <Td key={col.key}>
                  <SkeletonBar $width="55%" />
                </Td>
              ))}
            </Tr>
          ))}
        </tbody>
      </Table>
    </OverlayScroll>
  );
}

export function WorkersBody({
  data,
  loading,
  error,
  isRunning,
}: WorkersBodyProps) {
  const t = useWorkerTable(data?.workers ?? []);

  if (t.total === 0) {
    // Initial fetch in flight: shaped skeleton instead of a text flash.
    if (loading && !data) {
      return <WorkersSkeleton sort={t.sort} />;
    }
    return (
      <TabEmptyState>{emptyMessage(isRunning, !!error && !data)}</TabEmptyState>
    );
  }

  return (
    <>
      <WorkerTable
        workers={t.visibleWorkers}
        sort={t.sort}
        onSort={t.toggleSort}
      />
      {t.paginate ? (
        <TablePagination
          pageIndex={t.pageIndex}
          pageCount={t.pageCount}
          pageSize={t.pageSize}
          total={t.total}
          onPageChange={t.setPageIndex}
          onPageSizeChange={t.changePageSize}
        />
      ) : null}
    </>
  );
}
